/**
 * Analytics utility — GA4 + backend event tracking + heartbeat for real-time users.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Heartbeat cadence — keep in sync with backend's `realtime_users` window (60s)
const HEARTBEAT_INTERVAL_MS = 15000;

// Persistent session ID for heartbeat
const SESSION_ID = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Send event to GA4 */
function gtagEvent(name, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

/** Fire-and-forget POST to backend */
function backendTrack(event, data = {}) {
  const payload = {
    event,
    data,
    url: window.location.href,
    referrer: document.referrer || '',
    user_agent: navigator.userAgent,
    screen: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString(),
  };
  const params = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const val = params.get(key);
    if (val) payload[key] = val;
  });
  fetch(`${API_URL}/api/analytics/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

function track(event, data = {}) {
  gtagEvent(event, data);
  backendTrack(event, data);
}

// ── Heartbeat for real-time active users ──
let _heartbeatInterval = null;
let _heartbeatState = {};

function startHeartbeat(page, quizSection = '', quizQuestion = 0) {
  _heartbeatState = { page, quiz_section: quizSection, quiz_question: quizQuestion };
  // Send immediately, then on a regular cadence
  _sendHeartbeat();
  if (_heartbeatInterval) clearInterval(_heartbeatInterval);
  _heartbeatInterval = setInterval(_sendHeartbeat, HEARTBEAT_INTERVAL_MS);
}

function updateHeartbeat(quizSection, quizQuestion) {
  _heartbeatState.quiz_section = quizSection;
  _heartbeatState.quiz_question = quizQuestion;
}

function stopHeartbeat() {
  if (_heartbeatInterval) {
    clearInterval(_heartbeatInterval);
    _heartbeatInterval = null;
  }
}

function _sendHeartbeat() {
  fetch(`${API_URL}/api/analytics/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: SESSION_ID,
      user_agent: navigator.userAgent,
      ..._heartbeatState,
    }),
  }).catch(() => {});
}

// ── Public API ──

export const analytics = {
  pageView: (page) => track('page_view', { page }),

  quizStart: () => {
    track('quiz_start');
    startHeartbeat('quiz', '1', 0);
  },

  quizQuestionAnswer: (section, questionIndex) => {
    track('quiz_question_answer', { section, question_index: questionIndex });
    updateHeartbeat(String(section), questionIndex);
  },

  quizSectionComplete: (section, questionCount) => {
    track('quiz_section_complete', { section, question_count: questionCount });
    updateHeartbeat(String(section + 1), 0);
  },

  quizAbandoned: (section, questionIndex) => {
    track('quiz_abandoned', { section, question_index: questionIndex });
    stopHeartbeat();
  },

  quizSubmit: (primary, secondary) => {
    track('quiz_submit', { primary_archetype: primary, secondary_archetype: secondary });
    stopHeartbeat();
    startHeartbeat('results');
  },

  reportGenerate: (type, primary, secondary) =>
    track('report_generate', { report_type: type, primary_archetype: primary, secondary_archetype: secondary }),

  reportDownload: (reportId) =>
    track('report_download', { report_id: reportId }),

  paymentStart: (amount) =>
    track('payment_start', { amount }),

  paymentComplete: (paymentId, amount) =>
    track('payment_complete', { payment_id: paymentId, amount }),

  // Call when user leaves quiz (beforeunload)
  stopTracking: () => stopHeartbeat(),
};

// Stop heartbeat when user leaves the page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    stopHeartbeat();
  });
}
